import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { Date as DateModel } from '@lsq-frontend/remote-services/all-java/common-mk0';
import { Money } from '@lsq-frontend/remote-services/funding-api';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { convertDateToLocalDate } from '../../helpers/date-mapping';
import { multiplyMoney } from '../../helpers/money-operations';
import { mapBuyerPayableSortColumn } from '../../helpers/sort-column-mapping';
import {
    mapPayableStatusLegacyToEarlyPmtStatus,
    mapPayableStatusLegacyToInvoiceFundingStatus,
    mapPayableStatusToBuyerApprovalStatus
} from '../../helpers/status-mapping';
import { ListInvoiceSummaryForBuyerResponse } from '../../model/list-invoice-summary-for-buyer-response';
import { PayableStatusLegacy } from '../../model/models';
import { deDuplicatePendingCreditMemos } from '../../helpers/credit-memo-mapping';
import {
    InvoiceSummary2,
    PayablesInput,
    PayablesResponse,
    SortDirection
} from '../../model/models';

import { Pagination } from '@lsq-frontend/angular-shared/api-service';

export class ListGroupedInvoiceSummaryForBuyerRequest2 {
    public buyerKey: string;
    public refInvoiceKey: string;
    public canRevoke: boolean;
    public pagination: Pagination;
    public includePendingPayables: boolean;
    constructor(init?: Partial<ListGroupedInvoiceSummaryForBuyerRequest2>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}
@Injectable({ providedIn: 'root' })
export class ListGroupedInvoiceSummaryForBuyerService extends ProtoApiService<
    ListGroupedInvoiceSummaryForBuyerRequest2,
    ListInvoiceSummaryForBuyerResponse
> {
    public requestType = ListGroupedInvoiceSummaryForBuyerRequest2;
    public responseType = ListInvoiceSummaryForBuyerResponse;
    public endpointName = 'ListGroupedInvoiceSummaryForBuyer';
    public serviceName = 'financing';
    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'ListGroupedInvoiceSummaryForBuyer';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListGroupedInvoiceSummaryForBuyerRequest2
    ): { query: string } | ListGroupedInvoiceSummaryForBuyerRequest2 {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;

            const input: PayablesInput = omitBy(
                {
                    payableIds: [+req.refInvoiceKey],
                    companyFilter: {
                        buyerIds: [req.buyerKey]
                    },
                    pagination: {
                        skip: skip ?? 0,
                        take: take ?? 0,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] || SortDirection.ASCENDING
                        )
                    },
                    includePendingPayables: req.includePendingPayables
                } as PayablesInput,
                isNil
            );
            const money = { cents: true, currencyCode: true };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        id: true,
                        name: true,
                        status: true,
                        statusLegacy: true,
                        earlyPaymentDate: true,
                        dueInDays: true,
                        buyerDiscountFeeRate: true,
                        buyerDiscountFee: money,
                        supplierDiscountFee: money,
                        relationship: {
                            supplier: { id: true, company: { name: true } },
                            buyer: { id: true, company: { name: true } }
                        },
                        approvals: {
                            id: true,
                            date: true,
                            amount: money,
                            canBeRevoked: true,
                            originalDueDate: true,
                            payLaterRequested: true,
                            payLaterAdjustedDueDate: true,
                            amountLessSupplierDiscountFee: money,
                            invoice: {
                                id: true,
                                date: true,
                                invoiceNumber: true,
                                amount: money
                            }
                        },
                        creditMemos: {
                            id: true,
                            date: true,
                            canBeRevoked: true,
                            creditMemoNumber: true,
                            allocatedAmount: money,
                            amount: money
                        }
                    },
                    totals: { invoiceCount: true }
                }
            });

            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | ListInvoiceSummaryForBuyerResponse
    ): ListInvoiceSummaryForBuyerResponse {
        if (this._isPayLaterEnabled) {
            const { rows: payables, totals } = (res as GraphQLResponse<PayablesResponse>).data
                .payables;
            const payable = payables[0];

            let deduplicatedCreditMemos = payable.creditMemos;

            if (payable.approvals.length == 0 && payable.creditMemos.length > 0) {
                deduplicatedCreditMemos = deDuplicatePendingCreditMemos(payable.creditMemos);
            }

            return new ListInvoiceSummaryForBuyerResponse({
                ok: true,
                totalCountBuyerInvoices: totals.invoiceCount,
                rows: [
                    ...deduplicatedCreditMemos.map((creditMemo) => {
                        const { buyer, supplier } = payable.relationship;

                        let creditMemoAmount: Money;

                        if (payable.approvals.length > 0) {
                            if (creditMemo.allocatedAmount.cents == 0) {
                                creditMemoAmount = creditMemo.amount;
                            } else {
                                creditMemoAmount = creditMemo.allocatedAmount;
                            }
                        } else {
                            // Detect pending payable and sum up the remaining amount
                            creditMemoAmount = creditMemo.amount;
                        }

                        if (creditMemoAmount.cents > 0) {
                            creditMemoAmount = multiplyMoney(creditMemoAmount, -1);
                        }

                        const invoiceDate = convertDateToLocalDate(creditMemo.date);
                        const invoiceDateNew =
                            invoiceDate &&
                            new DateModel({
                                day: invoiceDate.getDate(),
                                month: invoiceDate.getMonth() + 1,
                                year: invoiceDate.getFullYear()
                            });

                        const invoiceDueDate = convertDateToLocalDate(payable.originalDueDate);
                        const invoiceDueDateNew =
                            invoiceDueDate &&
                            new DateModel({
                                day: invoiceDueDate.getDate(),
                                month: invoiceDueDate.getMonth() + 1,
                                year: invoiceDueDate.getFullYear()
                            });

                        const earlyPaymentDate = convertDateToLocalDate(payable.earlyPaymentDate);
                        const earlyPaymentDateNew =
                            earlyPaymentDate &&
                            new DateModel({
                                day: earlyPaymentDate.getDate(),
                                month: earlyPaymentDate.getMonth() + 1,
                                year: earlyPaymentDate.getFullYear()
                            });

                        return new InvoiceSummary2({
                            invoiceDate,
                            invoiceDateNew,
                            invoiceDueDate,
                            invoiceDueDateNew,
                            earlyPaymentDate,
                            earlyPaymentDateNew,
                            groupId: payable.name,
                            refInvoiceKey: payable.id,
                            refGroupedInvoiceKey: creditMemo.id.toString(),
                            buyerName: buyer.company.name,
                            invoiceNumber: creditMemo.creditMemoNumber,
                            dueIn: payable.dueInDays,
                            sellerName: supplier.company.name,
                            sellerKey: supplier.id,
                            refBuyerKey: buyer.id,
                            invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                                payable.statusLegacy
                            ),
                            fundingStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                                payable.statusLegacy
                            ),
                            buyerApprovalStatus: mapPayableStatusToBuyerApprovalStatus(
                                payable.status
                            ),
                            earlyPaymentStatus: mapPayableStatusLegacyToEarlyPmtStatus(
                                payable.statusLegacy
                            ),
                            discountAmount: payable.buyerDiscountFee,
                            buyerDiscountAmount: payable.buyerDiscountFee,
                            supplierDiscountAmount: payable.supplierDiscountFee,
                            buyerDiscountPercentage: payable.buyerDiscountFeeRate * 100,
                            feeRateHundredthOfBps: payable.buyerDiscountFeeRate * 100,
                            invoiceAmount: creditMemo.amount,
                            finalApprovedAmount: creditMemoAmount,
                            availableAmount: creditMemoAmount,
                            canBeRevoked:
                                creditMemo.canBeRevoked &&
                                payable.statusLegacy != PayableStatusLegacy.REVOKED
                        });
                    }),
                    ...payable.approvals.map((approval) => {
                        const { buyer, supplier } = payable.relationship;

                        const invoiceDate = convertDateToLocalDate(approval.invoice.date);
                        const invoiceDateNew =
                            invoiceDate &&
                            new DateModel({
                                day: invoiceDate.getDate(),
                                month: invoiceDate.getMonth() + 1,
                                year: invoiceDate.getFullYear()
                            });

                        const invoiceDueDate = convertDateToLocalDate(
                            approval.payLaterRequested
                                ? approval.payLaterAdjustedDueDate
                                : approval.originalDueDate
                        );
                        const invoiceDueDateNew =
                            invoiceDueDate &&
                            new DateModel({
                                day: invoiceDueDate.getDate(),
                                month: invoiceDueDate.getMonth() + 1,
                                year: invoiceDueDate.getFullYear()
                            });

                        const earlyPaymentDate = convertDateToLocalDate(payable.earlyPaymentDate);
                        const earlyPaymentDateNew =
                            earlyPaymentDate &&
                            new DateModel({
                                day: earlyPaymentDate.getDate(),
                                month: earlyPaymentDate.getMonth() + 1,
                                year: earlyPaymentDate.getFullYear()
                            });

                        const payLaterDueDate = convertDateToLocalDate(
                            approval.payLaterAdjustedDueDate
                        );

                        return new InvoiceSummary2({
                            isGroup: false,
                            invoiceDate,
                            invoiceDateNew,
                            invoiceDueDate,
                            invoiceDueDateNew,
                            earlyPaymentDate,
                            earlyPaymentDateNew,
                            payLaterDueDate,
                            refInvoiceKey: payable.id,
                            refGroupedInvoiceKey: approval.id.toString(),
                            buyerName: buyer.company.name,
                            invoiceNumber: approval.invoice.invoiceNumber,
                            dueIn: payable.dueInDays,
                            sellerName: supplier.company.name,
                            sellerKey: supplier.id,
                            refBuyerKey: buyer.id,
                            invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                                payable.statusLegacy
                            ),
                            fundingStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                                payable.statusLegacy
                            ),
                            buyerApprovalStatus: mapPayableStatusToBuyerApprovalStatus(
                                payable.status
                            ),
                            earlyPaymentStatus: mapPayableStatusLegacyToEarlyPmtStatus(
                                payable.statusLegacy
                            ),
                            discountAmount: payable.buyerDiscountFee,
                            buyerDiscountAmount: payable.buyerDiscountFee,
                            supplierDiscountAmount: payable.supplierDiscountFee,
                            buyerDiscountPercentage: payable.buyerDiscountFeeRate * 100,
                            feeRateHundredthOfBps: payable.buyerDiscountFeeRate * 100,
                            invoiceAmount: approval.invoice.amount,
                            finalApprovedAmount: approval.amount,
                            availableAmount: approval.amountLessSupplierDiscountFee,
                            canBeRevoked: approval.canBeRevoked,
                            buyerPaidTransactionFee: false // TODO: add to graphql
                        });
                    })
                ]
            });
        }
        return res as ListInvoiceSummaryForBuyerResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
